import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Save,
  Image,
  Folder,
  Type,
  X,
  Check,
  ChevronDown,
  ChevronRight,
  Code,
  Copy,
  ExternalLink,
  FileText,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  rectIntersection,
  useDroppable,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { api } from '../services/api'
import Button from '../components/Button'
import Input from '../components/Input'
import ImageUpload from '../components/ImageUpload'
import { useLanguageStore } from '../stores/languageStore'

function SortableItem({
  item,
  children,
  onEdit,
  onDelete,
  onAddChild,
  isOpen,
  onToggle,
  currentLanguageId,
  isOverGroup,
}) {
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.tempId || item.id })

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `droppable-${item.tempId || item.id}`,
    data: { type: 'nav_group', item },
    disabled: item.type !== 'nav_group',
  })

  const setNodeRef = (node) => {
    setSortableRef(node)
    if (item.type === 'nav_group') {
      setDroppableRef(node)
    }
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const showDropIndicator = item.type === 'nav_group' && (isOver || isOverGroup)

  const getItemLabel = () => {
    if (!item.translations || item.translations.length === 0) {
      return 'Untitled'
    }
    const translation = item.translations.find(
      (t) => t.language_id === currentLanguageId
    )
    return translation?.label || item.translations[0]?.label || 'Untitled'
  }

  const displayLabel = getItemLabel()

  const getTypeIcon = () => {
    switch (item.type) {
      case 'text':
        return <Type className="h-4 w-4" />
      case 'image':
        return <Image className="h-4 w-4" />
      case 'nav_group':
        return <Folder className="h-4 w-4" />
      case 'page':
        return <FileText className="h-4 w-4" />
      default:
        return <Type className="h-4 w-4" />
    }
  }

  const getTypeLabel = () => {
    switch (item.type) {
      case 'text':
        return 'Text'
      case 'image':
        return 'Image'
      case 'nav_group':
        return 'Nav Group'
      case 'page':
        return 'Page'
      default:
        return item.type
    }
  }

  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      <div
        className={`bg-white border rounded-lg p-4 transition-colors ${
          showDropIndicator
            ? 'border-primary-500 border-2 bg-primary-50'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="h-5 w-5 text-gray-400" />
          </div>

          {(item.type === 'nav_group' || (item.type === 'page' && children)) &&
            children && (
              <button
                onClick={() => onToggle(item.tempId || item.id)}
                className="text-gray-400 hover:text-gray-600"
              >
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            )}

          {item.image_url ? (
            <div className="flex items-center gap-3">
              <img
                src={item.image_url}
                alt={item.image_alt || displayLabel}
                className="h-16 w-16 object-contain border border-gray-200 rounded-lg bg-white p-1.5 shadow-sm"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-gray-500">
              {getTypeIcon()}
            </div>
          )}

          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{displayLabel}</span>
              <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-100 rounded">
                {getTypeLabel()}
              </span>
            </div>
            {item.url && (
              <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />
                <span className="truncate">{item.url}</span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            {(item.type === 'nav_group' ||
              (item.type === 'page' && item.parent_id)) && (
              <button
                onClick={() => onAddChild(item)}
                className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
              >
                + Add Item
              </button>
            )}
            <button
              onClick={() => onEdit(item)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(item.tempId || item.id)}
              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {(item.type === 'nav_group' || item.type === 'page') &&
        children &&
        isOpen && <div className="ml-12 mt-2 space-y-2">{children}</div>}
    </div>
  )
}

function RootDropZone({ children, isOver }) {
  const { setNodeRef } = useDroppable({
    id: 'root-droppable',
  })

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[100px] rounded-lg transition-colors ${
        isOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : ''
      }`}
    >
      {children}
    </div>
  )
}

function ItemForm({
  item,
  onSave,
  onCancel,
  languages,
  pages,
  isChild = false,
}) {
  const { currentLanguageId } = useLanguageStore()
  const [formData, setFormData] = useState(() => {
    if (item) {
      return {
        ...item,
        url: item.url || '',
        image_url: item.image_url || '',
        image_alt: item.image_alt || '',
        target: item.target || '_self',
        translations: item.translations || {},
      }
    }
    return {
      type: isChild ? 'page' : 'nav_group',
      url: '',
      page_id: null,
      image_url: '',
      image_alt: '',
      target: '_self',
      is_active: true,
      parent_id: null,
      translations: {},
    }
  })

  useEffect(() => {
    if (item && Array.isArray(languages)) {
      const translations = {}
      languages.forEach((lang) => {
        const translation = item.translations?.find(
          (t) => t.language_id === lang.id
        )
        translations[lang.id] = translation || {
          language_id: lang.id,
          label: '',
          url: '',
        }
      })
      setFormData((prev) => ({
        ...item,
        url: item.url || '',
        image_url: item.image_url || '',
        image_alt: item.image_alt || '',
        target: item.target || '_self',
        translations,
      }))
    } else if (!item && Array.isArray(languages)) {
      const translations = {}
      languages.forEach((lang) => {
        translations[lang.id] = { language_id: lang.id, label: '', url: '' }
      })
      setFormData((prev) => ({ ...prev, translations }))
    }
  }, [item, languages])

  const currentLang = languages.find((l) => l.id === currentLanguageId)

  const handleSubmit = () => {
    if (formData.type === 'image' && !formData.image_url) {
      alert('Please provide an image URL')
      return
    }

    if (formData.type === 'page' && !formData.page_id) {
      alert('Please select a page')
      return
    }

    const translationsArray = Object.values(formData.translations).filter((t) =>
      t.label?.trim()
    )

    if (translationsArray.length === 0) {
      alert('Please provide at least one label')
      return
    }

    onSave({
      ...formData,
      label: translationsArray[0].label,
      translations: translationsArray,
    })
  }

  const typeOptions = isChild
    ? [
        { value: 'text', label: 'Text', icon: Type },
        { value: 'image', label: 'Image', icon: Image },
        { value: 'page', label: 'Page', icon: FileText },
      ]
    : [
        { value: 'nav_group', label: 'Nav Group', icon: Folder },
        { value: 'text', label: 'Text', icon: Type },
        { value: 'image', label: 'Image', icon: Image },
        { value: 'page', label: 'Page', icon: FileText },
      ]

  return (
    <div className="mb-2 bg-white border-2 border-primary-500 rounded-lg p-4 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900">
          {item
            ? 'Edit Item'
            : isChild
            ? 'Add Item to Group'
            : 'New Navigation Item'}
        </h3>
        <button
          onClick={onCancel}
          className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type
          </label>
          <div
            className={`grid gap-2 ${isChild ? 'grid-cols-3' : 'grid-cols-4'}`}
          >
            {typeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setFormData({ ...formData, type: option.value })}
                className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                  formData.type === option.value
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <option.icon className="h-6 w-6" />
                <span className="text-sm font-medium">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formData.type === 'text' ? 'Text Content' : 'Label'}{' '}
            {currentLang && `(${currentLang.name})`}
          </label>
          <Input
            value={formData.translations[currentLanguageId]?.label || ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                translations: {
                  ...formData.translations,
                  [currentLanguageId]: {
                    ...formData.translations[currentLanguageId],
                    language_id: currentLanguageId,
                    label: e.target.value,
                  },
                },
              })
            }
            placeholder={
              formData.type === 'text' ? 'Enter text content' : 'Enter label'
            }
          />
        </div>

        {formData.type === 'image' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image
              </label>
              <ImageUpload
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                altText={formData.image_alt}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image Alt Text
              </label>
              <Input
                value={formData.image_alt}
                onChange={(e) =>
                  setFormData({ ...formData, image_alt: e.target.value })
                }
                placeholder="Logo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link URL (Optional)
              </label>
              <Input
                value={formData.url}
                onChange={(e) =>
                  setFormData({ ...formData, url: e.target.value })
                }
                placeholder="https://example.com (leave empty for no link)"
              />
            </div>
            {formData.url && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Open In
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setFormData({ ...formData, target: '_self' })
                    }
                    className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                      formData.target === '_self'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Same Tab
                  </button>
                  <button
                    onClick={() =>
                      setFormData({ ...formData, target: '_blank' })
                    }
                    className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                      formData.target === '_blank'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    New Tab
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {formData.type === 'page' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Page
              </label>
              <select
                value={formData.page_id || ''}
                onChange={(e) => {
                  const pageId = e.target.value
                    ? parseInt(e.target.value)
                    : null
                  const selectedPage = pages.find((p) => p.id === pageId)

                  const updatedTranslations = { ...formData.translations }
                  if (selectedPage && currentLanguageId) {
                    const pageTranslation = selectedPage.translations?.find(
                      (t) => t.language_id === currentLanguageId
                    )
                    const pageTitle =
                      pageTranslation?.title ||
                      selectedPage.translations?.[0]?.title ||
                      selectedPage.title ||
                      ''

                    updatedTranslations[currentLanguageId] = {
                      ...updatedTranslations[currentLanguageId],
                      language_id: currentLanguageId,
                      label: pageTitle,
                    }
                  }

                  setFormData({
                    ...formData,
                    page_id: pageId,
                    translations: updatedTranslations,
                  })
                }}
                className="w-full rounded-md border-gray-300"
              >
                <option value="">Select a page</option>
                {Array.isArray(pages) &&
                  pages.map((page) => {
                    const pageTranslation = page.translations?.find(
                      (t) => t.language_id === currentLanguageId
                    )
                    const displayTitle =
                      pageTranslation?.title ||
                      page.translations?.[0]?.title ||
                      page.title ||
                      'Untitled'
                    return (
                      <option key={page.id} value={page.id}>
                        {displayTitle}
                      </option>
                    )
                  })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Open In
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setFormData({ ...formData, target: '_self' })}
                  className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                    formData.target === '_self'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Same Tab
                </button>
                <button
                  onClick={() => setFormData({ ...formData, target: '_blank' })}
                  className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                    formData.target === '_blank'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  New Tab
                </button>
              </div>
            </div>
          </>
        )}

        <div className="flex items-center justify-between pt-4 border-t">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </label>

          <div className="flex gap-2">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              <Check className="mr-2 h-4 w-4" />
              {item ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function NavigationBuilder() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentLanguageId } = useLanguageStore()

  const [items, setItems] = useState([])
  const [editingItem, setEditingItem] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [addingChildTo, setAddingChildTo] = useState(null)
  const [expandedGroups, setExpandedGroups] = useState(new Set())
  const [showJsonModal, setShowJsonModal] = useState(false)
  const [jsonData, setJsonData] = useState(null)
  const [activeId, setActiveId] = useState(null)
  const [overGroupId, setOverGroupId] = useState(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  const { data: navigation, isLoading: navLoading } = useQuery({
    queryKey: ['navigation', slug],
    queryFn: () => api.get(`/navigations/${slug}`).then((res) => res.data),
  })

  const { data: languages = [] } = useQuery({
    queryKey: ['languages'],
    queryFn: async () => {
      const res = await api.get('/languages')
      const data = res.data.data || res.data
      return Array.isArray(data) ? data : []
    },
  })

  const { data: pages = [] } = useQuery({
    queryKey: ['pages'],
    queryFn: async () => {
      const res = await api.get('/pages')
      const data = res.data.data || res.data
      return Array.isArray(data) ? data : []
    },
  })

  const updateItemsMutation = useMutation({
    mutationFn: (items) => api.put(`/navigations/${slug}/items`, { items }),
    onSuccess: () => {
      queryClient.invalidateQueries(['navigation', slug])
    },
  })

  useEffect(() => {
    if (navigation?.all_items) {
      const processedItems = navigation.all_items.map((item, index) => ({
        ...item,
        tempId: item.id
          ? `db-${item.id}`
          : `temp-${Date.now()}-${index}-${Math.random()}`,
        parent_id: item.parent_id ? `db-${item.parent_id}` : null,
      }))
      setItems(processedItems)

      const groups = processedItems
        .filter((item) => item.type === 'nav_group')
        .map((item) => item.tempId)
      setExpandedGroups(new Set(groups))
    }
  }, [navigation])

  const handleAddItem = () => {
    setShowAddForm(true)
    setEditingItem(null)
    setAddingChildTo(null)
  }

  const handleAddChildToGroup = (parent) => {
    setAddingChildTo(parent)
    setShowAddForm(false)
    setEditingItem(null)
  }

  const handleEditItem = (item) => {
    setEditingItem(item)
    setShowAddForm(false)
    setAddingChildTo(null)
  }

  const handleSaveItem = (itemData) => {
    if (editingItem) {
      setItems(
        items.map((item) =>
          item.tempId === editingItem.tempId
            ? {
                ...itemData,
                tempId: item.tempId,
                parent_id: item.parent_id,
                id: item.id,
              }
            : item
        )
      )
      setEditingItem(null)
    } else if (addingChildTo) {
      const timestamp = Date.now()
      const newItem = {
        ...itemData,
        tempId: `temp-${timestamp}-${Math.random()}`,
        parent_id: addingChildTo.tempId,
        order: items.filter((i) => i.parent_id === addingChildTo.tempId).length,
      }
      setItems([...items, newItem])

      setExpandedGroups((prev) => {
        const newSet = new Set(prev)
        newSet.add(addingChildTo.tempId)
        return newSet
      })

      setAddingChildTo(null)
    } else {
      const timestamp = Date.now()
      const newItem = {
        ...itemData,
        tempId: `temp-${timestamp}-${Math.random()}`,
        order: items.filter((i) => !i.parent_id).length,
      }
      setItems([...items, newItem])
      setShowAddForm(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingItem(null)
    setShowAddForm(false)
    setAddingChildTo(null)
  }

  const handleDeleteItem = (tempId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setItems(
        items.filter(
          (item) => item.tempId !== tempId && item.parent_id !== tempId
        )
      )
    }
  }

  const handleToggleGroup = (tempId) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(tempId)) {
        newSet.delete(tempId)
      } else {
        newSet.add(tempId)
      }
      return newSet
    })
  }

  const handleDragStart = (event) => {
    setActiveId(event.active.id)
  }

  const handleDragOver = (event) => {
    const { over } = event
    if (over && over.id.toString().startsWith('droppable-')) {
      const groupId = over.id.toString().replace('droppable-', '')
      setOverGroupId(groupId)
    } else if (over && over.id === 'root-droppable') {
      setOverGroupId('root')
    } else {
      setOverGroupId(null)
    }
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    setActiveId(null)
    setOverGroupId(null)

    if (!over) return

    const activeItem = items.find(
      (item) => (item.tempId || item.id) === active.id
    )
    if (!activeItem) return

    if (over.id.toString().startsWith('droppable-')) {
      const targetGroupId = over.id.toString().replace('droppable-', '')
      if (
        activeItem.tempId === targetGroupId ||
        activeItem.id === targetGroupId
      )
        return
      if (activeItem.type === 'nav_group') return

      setItems((prevItems) => {
        const updated = prevItems.map((item) => {
          if ((item.tempId || item.id) === active.id) {
            return { ...item, parent_id: targetGroupId }
          }
          return item
        })
        return reorderItems(updated)
      })

      setExpandedGroups((prev) => {
        const newSet = new Set(prev)
        newSet.add(targetGroupId)
        return newSet
      })
    } else if (over.id === 'root-droppable') {
      setItems((prevItems) => {
        const updated = prevItems.map((item) => {
          if ((item.tempId || item.id) === active.id) {
            return { ...item, parent_id: null }
          }
          return item
        })
        return reorderItems(updated)
      })
    } else if (active.id !== over.id) {
      const overItem = items.find(
        (item) => (item.tempId || item.id) === over.id
      )
      if (!overItem) return

      setItems((prevItems) => {
        const activeIndex = prevItems.findIndex(
          (item) => (item.tempId || item.id) === active.id
        )
        const overIndex = prevItems.findIndex(
          (item) => (item.tempId || item.id) === over.id
        )

        let updated = [...prevItems]
        updated[activeIndex] = {
          ...updated[activeIndex],
          parent_id: overItem.parent_id,
        }

        updated = arrayMove(updated, activeIndex, overIndex)
        return reorderItems(updated)
      })
    }
  }

  const reorderItems = (itemsList) => {
    const rootItems = itemsList.filter((i) => !i.parent_id)
    const childrenByParent = {}

    itemsList.forEach((item) => {
      if (item.parent_id) {
        if (!childrenByParent[item.parent_id]) {
          childrenByParent[item.parent_id] = []
        }
        childrenByParent[item.parent_id].push(item)
      }
    })

    return itemsList.map((item) => {
      if (!item.parent_id) {
        return { ...item, order: rootItems.indexOf(item) }
      }
      const siblings = childrenByParent[item.parent_id] || []
      return { ...item, order: siblings.indexOf(item) }
    })
  }

  const handleViewJson = async () => {
    try {
      const response = await api.get(`/navigations/${slug}`)
      setJsonData(response.data)
      setShowJsonModal(true)
    } catch (error) {
      alert('Error fetching navigation JSON: ' + error.message)
    }
  }

  const handleCloneFromPrimary = async () => {
    const primaryLang = languages.find((l) => l.is_default)
    const currentLang = languages.find((l) => l.id === currentLanguageId)

    if (!primaryLang || !currentLang) {
      alert('Cannot identify primary or current language')
      return
    }

    if (primaryLang.id === currentLang.id) {
      alert('You are already viewing the primary language')
      return
    }

    if (
      !window.confirm(
        `Clone all navigation items from ${primaryLang.name} to ${currentLang.name}? This will skip pages without ${currentLang.name} translations.`
      )
    ) {
      return
    }

    try {
      const pagesResponse = await api.get('/pages')
      const allPages = pagesResponse.data.data || pagesResponse.data || []

      const clonedItems = []

      for (const item of items) {
        const primaryTranslation = item.translations?.find(
          (t) => t.language_id === primaryLang.id
        )

        if (!primaryTranslation) continue

        const existingTranslation = item.translations?.find(
          (t) => t.language_id === currentLang.id
        )

        if (existingTranslation && existingTranslation.label?.trim()) {
          continue
        }

        if (item.type === 'page' && item.page_id) {
          const page = allPages.find((p) => p.id === item.page_id)
          if (!page) continue

          const pageHasCurrentLocale = page.available_locales?.includes(
            currentLang.code
          )

          if (!pageHasCurrentLocale) {
            continue
          }
        }

        clonedItems.push({
          itemId: item.id,
          translation: {
            language_id: currentLang.id,
            label: primaryTranslation.label,
            url: primaryTranslation.url || '',
          },
        })
      }

      if (clonedItems.length === 0) {
        alert(
          'No items to clone. All items either already exist or pages lack translations.'
        )
        return
      }

      setItems((prevItems) =>
        prevItems.map((item) => {
          const cloned = clonedItems.find((c) => c.itemId === item.id)
          if (!cloned) return item

          const updatedTranslations = [...(item.translations || [])]
          const existingIndex = updatedTranslations.findIndex(
            (t) => t.language_id === currentLang.id
          )

          if (existingIndex >= 0) {
            updatedTranslations[existingIndex] = cloned.translation
          } else {
            updatedTranslations.push(cloned.translation)
          }

          return { ...item, translations: updatedTranslations }
        })
      )

      alert(
        `Cloned ${clonedItems.length} items from ${primaryLang.name}. Don't forget to save!`
      )
    } catch (error) {
      alert(
        'Error cloning items: ' +
          (error.response?.data?.message || error.message)
      )
    }
  }

  const handleSaveNavigation = async () => {
    const idMapping = {}

    items.forEach((item) => {
      if (item.id && typeof item.id === 'number') {
        idMapping[item.tempId] = item.id
      }
    })

    const resolveParentId = (parentId) => {
      if (!parentId) return null
      if (typeof parentId === 'number') return parentId
      if (parentId.startsWith('db-')) {
        return parseInt(parentId.replace('db-', ''))
      }
      return idMapping[parentId] || null
    }

    const parentItems = items.filter((i) => !i.parent_id)
    const childItems = items.filter((i) => i.parent_id)

    const buildItemData = (item, index) => {
      const baseItem = {
        order: index,
        type: item.type,
        url: item.url || null,
        page_id: item.page_id || null,
        target: item.target || '_self',
        is_active: item.is_active,
        parent_id: resolveParentId(item.parent_id),
        image_url: item.image_url || null,
        image_alt: item.image_alt || null,
        translations: item.translations || [],
        _temp_id: item.tempId,
      }

      if (item.id && typeof item.id === 'number') {
        baseItem.id = item.id
      }

      if (item.parent_id && !resolveParentId(item.parent_id)) {
        baseItem._temp_parent_id = item.parent_id
      }

      return baseItem
    }

    const allItems = [...parentItems, ...childItems]
    const itemsToSave = allItems.map((item, index) =>
      buildItemData(item, index)
    )

    try {
      await updateItemsMutation.mutateAsync(itemsToSave)
      alert('Navigation saved successfully!')
    } catch (error) {
      alert(
        'Error saving navigation: ' +
          (error.response?.data?.message || error.message)
      )
    }
  }

  const renderItems = (parentId = null) => {
    const filteredItems = items.filter((item) => {
      if (item.parent_id !== parentId) return false
      return true
    })

    return filteredItems.map((item) => {
      const isEditingThis = editingItem && editingItem.tempId === item.tempId
      const isAddingChildToThis =
        addingChildTo && addingChildTo.tempId === item.tempId
      const children = items.filter((i) => i.parent_id === item.tempId)
      const isOpen = expandedGroups.has(item.tempId)

      if (isEditingThis) {
        return (
          <ItemForm
            key={item.tempId || item.id}
            item={editingItem}
            onSave={handleSaveItem}
            onCancel={handleCancelEdit}
            languages={languages}
            pages={pages}
          />
        )
      }

      return (
        <div key={item.tempId || item.id}>
          <SortableItem
            item={item}
            onEdit={handleEditItem}
            onDelete={handleDeleteItem}
            onAddChild={handleAddChildToGroup}
            onToggle={handleToggleGroup}
            isOpen={isOpen}
            currentLanguageId={currentLanguageId}
            isOverGroup={overGroupId === (item.tempId || item.id)}
            children={
              children.length > 0 || isAddingChildToThis ? (
                <>
                  {isAddingChildToThis && (
                    <ItemForm
                      onSave={handleSaveItem}
                      onCancel={handleCancelEdit}
                      languages={languages}
                      pages={pages}
                      isChild={true}
                    />
                  )}
                  {renderItems(item.tempId)}
                </>
              ) : null
            }
          />
        </div>
      )
    })
  }

  if (navLoading) {
    return <div className="text-center py-8">Loading navigation...</div>
  }

  const topLevelItems = items.filter((item) => {
    if (item.parent_id) return false
    return true
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/navigations')}
            className="text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {navigation?.name}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Build and manage navigation items
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!showAddForm && !editingItem && !addingChildTo && (
            <>
              <Button onClick={handleAddItem}>
                <Plus className="mr-2 h-4 w-4" />
                Add Item
              </Button>
              {!languages.find((l) => l.id === currentLanguageId)
                ?.is_default && (
                <Button variant="outline" onClick={handleCloneFromPrimary}>
                  <Copy className="mr-2 h-4 w-4" />
                  Clone from Primary
                </Button>
              )}
            </>
          )}
          <Button variant="outline" onClick={handleViewJson}>
            <Code className="mr-2 h-4 w-4" />
            View JSON
          </Button>
          <Button
            onClick={handleSaveNavigation}
            disabled={updateItemsMutation.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            {updateItemsMutation.isPending ? 'Saving...' : 'Save Navigation'}
          </Button>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        {showAddForm && (
          <ItemForm
            onSave={handleSaveItem}
            onCancel={handleCancelEdit}
            languages={languages}
            pages={pages}
          />
        )}

        {topLevelItems.length === 0 && !showAddForm ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-gray-500 mb-4">No navigation items yet</div>
            <Button onClick={handleAddItem}>
              <Plus className="mr-2 h-4 w-4" />
              Add First Item
            </Button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((i) => i.tempId || i.id)}
              strategy={verticalListSortingStrategy}
            >
              <RootDropZone isOver={overGroupId === 'root'}>
                {renderItems()}
              </RootDropZone>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {showJsonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                Navigation JSON Response
              </h3>
              <button
                onClick={() => setShowJsonModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <pre className="text-xs bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto">
                {JSON.stringify(jsonData, null, 2)}
              </pre>
            </div>
            <div className="p-4 border-t flex justify-end">
              <Button
                variant="secondary"
                onClick={() => setShowJsonModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
